import { validatePAN } from '../../../../../../utils/validatePAN';

/* The displayed entity type for a company, mapped from the raw inputs the overview response
   carries. Nothing here is persisted — constitutionType is what PEC eligibility, state-machine
   gating and BGV routing run on, and it is never rewritten from what is shown.

   constitutionType wins whenever present, and a present-but-unmapped value resolves to nothing
   rather than falling through, so a PAN never overrides a stored type.

   Case is folded with toUpperCase/toLowerCase, which are locale-independent per spec. The
   toLocale* variants are NOT: under a Turkish locale the dotless i would break every lookup. */

/** The company-type dropdown options — these strings are the godzilla option `_id`s, and must stay
    byte-identical to aica's CommonConstants.CompanyTypeLabel, which writes them to the PEC. */
export const COMPANY_TYPE_LABELS = {
    INDIVIDUAL: 'Individual',
    TRUST: 'Trust',
    LOCAL_AUTHORITY: 'Local Authority',
    HINDU_UNDIVIDED_FAMILY: 'Hindu Undivided Family',
    BODY_OF_INDIVIDUALS: 'Body of Individuals',
    ASSOCIATION_OF_PERSONS: 'Association of Persons',
    ARTIFICIAL_JURIDICAL_PERSON: 'Artificial Juridical Person',
    GOVERNMENT: 'Government',
    PUBLIC: 'Public',
    PRIVATE_LIMITED: 'Private Limited',
    LIMITED_LIABILITY_PARTNER: 'Limited Liability Partner',
    PARTNERSHIP: 'Partnership',
    SOLE_PROPRIETORSHIP: 'Sole proprietorship',
} as const;

/** Shown for a C-PAN carrying no stored constitution type. Deliberately outside the labels above —
    it is not one of the options the company-type dropdown offers. */
const ENTITY_TYPE_COMPANY = 'Company';

const CONSTITUTION_TYPE_LABELS: Record<string, string> = {
    INDIVIDUAL: COMPANY_TYPE_LABELS.INDIVIDUAL,
    TRUST: COMPANY_TYPE_LABELS.TRUST,
    LOCAL_AUTHORITY: COMPANY_TYPE_LABELS.LOCAL_AUTHORITY,
    HINDU_UNDIVIDED_FAMILY: COMPANY_TYPE_LABELS.HINDU_UNDIVIDED_FAMILY,
    BODY_OF_INDIVIDUALS: COMPANY_TYPE_LABELS.BODY_OF_INDIVIDUALS,
    ASSOCIATION_OF_PERSONS: COMPANY_TYPE_LABELS.ASSOCIATION_OF_PERSONS,
    ARTIFICIAL_JURIDICAL_PERSON: COMPANY_TYPE_LABELS.ARTIFICIAL_JURIDICAL_PERSON,
    GOVERNMENT: COMPANY_TYPE_LABELS.GOVERNMENT,
    LIMITED_LIABILITY_PARTNERSHIP: COMPANY_TYPE_LABELS.LIMITED_LIABILITY_PARTNER,
    FIRM: COMPANY_TYPE_LABELS.PARTNERSHIP,
    PARTNERSHIP: COMPANY_TYPE_LABELS.PARTNERSHIP,
    PERSON: COMPANY_TYPE_LABELS.SOLE_PROPRIETORSHIP,
    PROPRIETARY_CONCERN: COMPANY_TYPE_LABELS.SOLE_PROPRIETORSHIP,
};

/** PAN holder-type character -> label, for the characters that need no further input. */
const PAN_CHAR_LABELS: Record<string, string> = {
    C: ENTITY_TYPE_COMPANY,
    T: COMPANY_TYPE_LABELS.TRUST,
    H: COMPANY_TYPE_LABELS.HINDU_UNDIVIDED_FAMILY,
    A: COMPANY_TYPE_LABELS.ASSOCIATION_OF_PERSONS,
    B: COMPANY_TYPE_LABELS.BODY_OF_INDIVIDUALS,
    L: COMPANY_TYPE_LABELS.LOCAL_AUTHORITY,
    J: COMPANY_TYPE_LABELS.ARTIFICIAL_JURIDICAL_PERSON,
    G: COMPANY_TYPE_LABELS.GOVERNMENT,
};

const LLPIN = /^[A-Za-z]{3}-\d{4}$/;

export interface EntityTypeInputs {
    constitutionType?: string;
    /** The registry classification, from customFields — the only signal that splits a public COMPANY from a private one. */
    classification?: string;
    panNumber?: string;
    cinNumber?: string;
    hasGstRegistration?: boolean;
}

/**
 * The registry classification is the only signal in the platform that separates a public company
 * from a private one — no CIN category parsing exists anywhere. Every other classification
 * (One Person, Nidhi, Not for Profit, Government) resolves to nothing rather than to a guess.
 */
const resolveCompany = (classification?: string): string | undefined => {
    const normalised = classification?.trim().toLowerCase() ?? '';
    if (normalised.startsWith('public')) return COMPANY_TYPE_LABELS.PUBLIC;
    if (normalised.startsWith('private')) return COMPANY_TYPE_LABELS.PRIVATE_LIMITED;
    return undefined;
};

const resolveFromPan = ({ panNumber, cinNumber, hasGstRegistration }: EntityTypeInputs): string | undefined => {
    // validatePAN tests the raw input, so folding is this caller's job — the contract isIndividualPan uses.
    const pan = panNumber?.trim().toUpperCase() ?? '';
    // Whole-string, never a substring: panNumber holds a GSTIN on some orgs, whose holder-type
    // character sits at index 5 rather than 3, so a substring match mislabels about a quarter of them.
    if (!validatePAN(pan)) return undefined;

    const holderType = pan.charAt(3);
    if (holderType === 'F') {
        return LLPIN.test(cinNumber?.trim() ?? '') ? COMPANY_TYPE_LABELS.LIMITED_LIABILITY_PARTNER : COMPANY_TYPE_LABELS.PARTNERSHIP;
    }
    if (holderType === 'P') {
        return hasGstRegistration ? COMPANY_TYPE_LABELS.SOLE_PROPRIETORSHIP : COMPANY_TYPE_LABELS.INDIVIDUAL;
    }
    return PAN_CHAR_LABELS[holderType];
};

/**
 * @returns the display label, or undefined when nothing resolves — the caller then renders no row
 *          at all rather than a blank one or a placeholder.
 */
export const resolveEntityType = (inputs: EntityTypeInputs): string | undefined => {
    const constitutionType = inputs.constitutionType?.trim().toUpperCase() ?? '';
    if (!constitutionType) return resolveFromPan(inputs);
    // Present-but-unmapped returns nothing on purpose: the PAN must not override a stored type.
    return constitutionType === 'COMPANY' ? resolveCompany(inputs.classification) : CONSTITUTION_TYPE_LABELS[constitutionType];
};
